import React, { useMemo } from 'react';

export interface GridCoord {
  x: number;
  y: number;
}

export interface PatternConfig {
  baseSquares: GridCoord[];
  deltaSquares: GridCoord[];
  deltaOffset: GridCoord;
  stepsToShow: number;
}

interface ComputedSquare {
  x: number;
  y: number;
}

interface ComputedStep {
  stepNumber: number;
  squares: ComputedSquare[];
  width: number;
  height: number;
  minX: number;
  minY: number;
}

interface PatternBuilderProps {
  config: PatternConfig;
  squareSize?: number;
  color?: string;
  stepGap?: number;
  showLabels?: boolean;
  highlightStep?: number;
  fadeSteps?: number[];
}

const PatternBuilder: React.FC<PatternBuilderProps> = ({
  config,
  squareSize = 20,
  color = '#0ea5e9',
  stepGap = 24,
  showLabels = true,
  highlightStep,
  fadeSteps = [],
}) => {
  const computeStepSquares = (cfg: PatternConfig, stepNumber: number): ComputedSquare[] => {
    const allSquares: ComputedSquare[] = [];

    // Add base squares
    for (const coord of cfg.baseSquares) {
      allSquares.push({ x: coord.x, y: coord.y });
    }

    // Add delta squares for each step from 2 onwards
    for (let s = 2; s <= stepNumber; s++) {
      const offsetMultiplier = s - 1;
      for (const delta of cfg.deltaSquares) {
        allSquares.push({
          x: delta.x + cfg.deltaOffset.x * offsetMultiplier,
          y: delta.y + cfg.deltaOffset.y * offsetMultiplier,
        });
      }
    }

    return allSquares;
  };

  const steps = useMemo<ComputedStep[]>(() => {
    const result: ComputedStep[] = [];

    for (let step = 1; step <= config.stepsToShow; step++) {
      const squares = computeStepSquares(config, step);

      // Calculate bounds
      const xs = squares.map(s => s.x);
      const ys = squares.map(s => s.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      result.push({
        stepNumber: step,
        squares,
        width: maxX - minX + 1,
        height: maxY - minY + 1,
        minX,
        minY,
      });
    }

    return result;
  }, [config]);

  return (
    <div className="flex items-end justify-center flex-wrap" style={{ gap: `${stepGap}px` }}>
      {steps.map((step) => {
        const isHighlighted = highlightStep === step.stepNumber;
        const isFaded = fadeSteps.includes(step.stepNumber);
        const containerWidth = step.width * squareSize;
        const containerHeight = step.height * squareSize;

        return (
          <div
            key={step.stepNumber}
            className="flex flex-col items-center"
            style={{
              opacity: isFaded ? 0.3 : 1,
              transition: 'opacity 0.3s ease',
            }}
          >
            {/* Pattern grid */}
            <div
              className="relative"
              style={{
                width: `${containerWidth}px`,
                height: `${containerHeight}px`,
                border: isHighlighted ? '2px solid rgba(14, 165, 233, 0.5)' : 'none',
                borderRadius: '4px',
                padding: isHighlighted ? '4px' : '0',
              }}
            >
              {step.squares.map((square, idx) => {
                const x = (square.x - step.minX) * squareSize;
                // Flip Y so patterns grow upward
                const y = (step.height - 1 - (square.y - step.minY)) * squareSize;

                return (
                  <div
                    key={idx}
                    className="absolute rounded-sm"
                    style={{
                      left: `${x}px`,
                      top: `${y}px`,
                      width: `${squareSize - 2}px`,
                      height: `${squareSize - 2}px`,
                      backgroundColor: color,
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  />
                );
              })}
            </div>

            {/* Step label */}
            {showLabels && (
              <span
                className="text-xs mt-2"
                style={{
                  color: isHighlighted ? '#0ea5e9' : '#64748b',
                  fontWeight: isHighlighted ? 'bold' : 'normal',
                }}
              >
                Step {step.stepNumber}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PatternBuilder;
